import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Skeleton } from '@mui/material';

import IRDataGrid from '../Common/IRDataGrid';
import { WorkQueueColumnDefinitions } from '../Common/DataGrid/ColumnDefinitions/WorkQueueColumnDefinitions';

const SKELETON_ROWS = 10;

const getTaskRows = (tasks) =>
	tasks.map((task) => ({
		id: task.id,
		task,
		subject: task.subject ?? '',
		assigned_to: task.owner_name ?? '',
		status: task.status ?? '',
		priority: task.priority ?? '',
		due_date: task.due_date ?? '',
		related_to: task.related_to ?? '',
		name: task.name ?? '',
		completed_at: task.completed_at ?? null,
	}));

const WorkQueueTable = ({
	tasks = [],
	loading = false,
	refreshing = false,
	loadingMore = false,
	hasMore = false,
	onLoadMore,
	onOpenTaskDrawer,
	sortModel,
	onSortModelChange,
}) => {
	const [rowSelectionModel, setRowSelectionModel] = useState([]);

	const columns = useMemo(() => WorkQueueColumnDefinitions(), []);

	const rows = useMemo(() => getTaskRows(tasks), [tasks]);

	const handleRowsScrollEnd = () => {
		if (!loading && !refreshing && !loadingMore && hasMore && onLoadMore) {
			onLoadMore();
		}
	};

	const handleCellClick = (params) => {
		if (params.field === 'subject' && params.row.task) {
			onOpenTaskDrawer(params.row.task);
		}
	};

	return (
		<Box
			sx={{
				width: '100%',
				position: 'relative',
				pb: '12px',
				minHeight: loading ? '500px' : undefined,
			}}
		>
			<IRDataGrid
				rows={rows}
				columns={columns}
				loading={false}
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				checkboxSelection
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={setRowSelectionModel}
				onRowsScrollEnd={handleRowsScrollEnd}
				scrollEndThreshold={100}
				noRowsMessage={loading ? '' : 'No work queue items found'}
				dataTestId='work-queue-table'
				minHeight={200}
				maxHeight='min(65vh, 700px)'
				onCellClick={handleCellClick}
				sx={{
					'& .MuiDataGrid-cell[data-field="subject"]': {
						cursor: 'pointer',
					},
				}}
			/>

			{/* Skeleton overlay: cover rows, but keep real column headers visible */}
			{loading && (
				<Box
					data-testid='work-queue-table-skeleton'
					sx={{
						position: 'absolute',
						top: '48px',
						left: 0,
						right: 0,
						height: `${SKELETON_ROWS * 44}px`,
						zIndex: 2,
						backgroundColor: 'background.paper',
						overflow: 'hidden',
						pointerEvents: 'auto',
					}}
				>
					{Array.from({ length: SKELETON_ROWS }, (_, index) => (
						<Box
							key={index}
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 2,
								px: 2,
								height: 44,
								borderBottom: '1px solid',
								borderColor: 'divider',
							}}
						>
							<Skeleton
								variant='rectangular'
								width={18}
								height={18}
								sx={{ flexShrink: 0 }}
							/>

							{[160, 110, 90, 90, 100, 130].map((width, columnIndex) => (
								<Box key={columnIndex} sx={{ flex: 1, minWidth: 0 }}>
									<Skeleton
										variant='text'
										width={`${Math.min(width / 2, 90)}%`}
										height={20}
									/>
								</Box>
							))}
						</Box>
					))}
				</Box>
			)}

			{/* Keep existing rows visible during refresh or pagination */}
			{(loadingMore || refreshing) && (
				<Box
					sx={{
						position: 'absolute',
						inset: 0,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						backgroundColor: 'rgba(255, 255, 255, 0.35)',
						pointerEvents: 'auto',
						zIndex: 2,
					}}
				>
					<CircularProgress size={28} thickness={4} />
				</Box>
			)}
		</Box>
	);
};

WorkQueueTable.propTypes = {
	tasks: PropTypes.arrayOf(PropTypes.object),
	loading: PropTypes.bool,
	refreshing: PropTypes.bool,
	loadingMore: PropTypes.bool,
	hasMore: PropTypes.bool,
	onLoadMore: PropTypes.func,
	onOpenTaskDrawer: PropTypes.func.isRequired,
	sortModel: PropTypes.arrayOf(
		PropTypes.shape({
			field: PropTypes.string.isRequired,
			sort: PropTypes.oneOf(['asc', 'desc']).isRequired,
		})
	).isRequired,
	onSortModelChange: PropTypes.func.isRequired,
};

export default WorkQueueTable;
